import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  HouseLine, 
  Key, 
  Wrench, 
  Calendar, 
  User, 
  Phone, 
  Envelope, 
  MapPin,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  SpinnerGap
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { useCookieConsent } from '../context/CookieConsentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formSchema = z.object({
  property_type: z.string().min(1, 'Bitte wählen Sie eine Option'),
  ownership_status: z.string().min(1, 'Bitte wählen Sie eine Option'),
  renovation_scope: z.string().min(1, 'Bitte wählen Sie eine Option'),
  project_timeline: z.string().min(1, 'Bitte wählen Sie eine Option'),
  first_name: z.string().min(1, 'Vorname ist erforderlich'),
  last_name: z.string().min(1, 'Nachname ist erforderlich'),
  phone: z.string().min(5, 'Gültige Telefonnummer erforderlich'),
  email: z.string().email('Gültige E-Mail-Adresse erforderlich'),
  postal_code: z.string().min(4, 'PLZ ist erforderlich'),
  city: z.string().min(1, 'Ort ist erforderlich'),
  privacy_accepted: z.literal(true, {
    errorMap: () => ({ message: 'Datenschutzerklärung muss akzeptiert werden' })
  }),
  marketing_consent: z.boolean().default(false)
});

const steps = [
  { id: 1, title: 'Immobilie', icon: HouseLine },
  { id: 2, title: 'Status', icon: Key },
  { id: 3, title: 'Umfang', icon: Wrench },
  { id: 4, title: 'Zeitplan', icon: Calendar },
  { id: 5, title: 'Kontakt', icon: User }
];

const propertyTypes = [
  { value: 'reihenhaus', label: 'Reihenhaus' },
  { value: 'doppelhaushaelfte', label: 'Doppelhaushälfte' },
  { value: 'eigentumswohnung', label: 'Eigentumswohnung' },
  { value: 'sonstiges', label: 'Sonstiges' }
];

const ownershipStatuses = [
  { value: 'eigentum', label: 'Bereits Eigentum' },
  { value: 'kaufprozess', label: 'Noch im Kaufprozess' }
];

const renovationScopes = [
  { value: 'komplettsanierung', label: 'Komplettsanierung' },
  { value: 'teilmodernisierung', label: 'Teilmodernisierung' },
  { value: 'unsicher', label: 'Ich bin noch unsicher' }
];

const projectTimelines = [
  { value: 'sofort', label: 'Sofort' },
  { value: '3_monate', label: 'In den nächsten 3 Monaten' },
  { value: '3_6_monate', label: 'In 3–6 Monaten' },
  { value: 'spaeter', label: 'Später' }
];

const LeadFormSection = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { trackEvent } = useCookieConsent();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      property_type: '',
      ownership_status: '',
      renovation_scope: '',
      project_timeline: '',
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      postal_code: '',
      city: '',
      privacy_accepted: false,
      marketing_consent: false
    }
  });

  const watchedValues = watch();

  const validateStep = async (step) => {
    const fieldsByStep = {
      1: ['property_type'],
      2: ['ownership_status'],
      3: ['renovation_scope'],
      4: ['project_timeline'],
      5: ['first_name', 'last_name', 'phone', 'email', 'postal_code', 'city', 'privacy_accepted']
    };
    return await trigger(fieldsByStep[step]);
  };

  const nextStep = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid && currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/leads`, data);
      if (response.data.success) {
        setIsSuccess(true);
        trackEvent('Lead', {
          content_name: 'Lead Form Submission',
          property_type: data.property_type,
          renovation_scope: data.renovation_scope
        });
        toast.success('Anfrage erfolgreich gesendet!');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const RadioOption = ({ name, value, label, currentValue }) => (
    <label
      className={`flex items-center gap-4 p-4 cursor-pointer border transition-all ${
        currentValue === value
          ? 'border-gold bg-gold/10'
          : 'border-white/10 hover:border-white/20 bg-white/5'
      }`}
      data-testid={`radio-${name}-${value}`}
    >
      <input
        type="radio"
        value={value}
        {...register(name)}
        className="hidden"
      />
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
        currentValue === value ? 'border-gold' : 'border-white/30'
      }`}>
        {currentValue === value && (
          <div className="w-2.5 h-2.5 rounded-full bg-gold" />
        )}
      </div>
      <span className="text-white text-sm">{label}</span>
    </label>
  );

  if (isSuccess) {
    return (
      <section id="lead-form" className="py-24 md:py-32 bg-dark-secondary" data-testid="lead-form-success">
        <div className="max-w-xl mx-auto px-4 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <CheckCircle size={40} className="text-green-400" weight="fill" />
          </motion.div>
          <h2 className="font-serif text-3xl text-white mb-4">
            Vielen Dank für Ihre Anfrage!
          </h2>
          <p className="text-white/60 mb-8">
            Wir haben Ihre Daten erhalten und melden uns schnellstmöglich bei Ihnen. 
            In der Regel innerhalb von 24 Stunden.
          </p>
          <a
            href="tel:+4921293758775"
            className="inline-flex items-center gap-2 text-gold hover:text-gold-light transition-colors"
          >
            <Phone size={18} />
            Oder rufen Sie uns direkt an: 02129/375877-5
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="lead-form" className="py-24 md:py-32 bg-dark-secondary" data-testid="lead-form-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-gold font-sans text-sm uppercase tracking-[0.2em] mb-4 block">
            Kostenlose Beratung
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4">
            Jetzt unverbindlich anfragen
          </h2>
          <p className="text-white/60 max-w-lg mx-auto">
            Beantworten Sie uns wenige Fragen und wir melden uns mit einem 
            individuellen Angebot bei Ihnen.
          </p>
        </motion.div>

        {/* Step Indicator */}
        <div className="flex justify-between mb-8 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-white/10" />
          {steps.map((step, index) => (
            <div key={step.id} className="relative z-10 flex flex-col items-center">
              <div
                className={`w-10 h-10 flex items-center justify-center transition-all ${
                  currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : currentStep === step.id
                    ? 'bg-gold text-black'
                    : 'bg-dark-tertiary text-white/50'
                }`}
              >
                {currentStep > step.id ? (
                  <CheckCircle size={20} weight="bold" />
                ) : (
                  <step.icon size={20} weight="light" />
                )}
              </div>
              <span className={`text-xs mt-2 hidden sm:block ${
                currentStep >= step.id ? 'text-white' : 'text-white/40'
              }`}>
                {step.title}
              </span>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="glass-card p-6 sm:p-8 md:p-10">
          <AnimatePresence mode="wait">
            {/* Step 1: Property Type */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-serif text-xl text-white mb-6">
                  Um welche Art von Immobilie handelt es sich?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {propertyTypes.map((type) => (
                    <RadioOption
                      key={type.value}
                      name="property_type"
                      value={type.value}
                      label={type.label}
                      currentValue={watchedValues.property_type}
                    />
                  ))}
                </div>
                {errors.property_type && (
                  <p className="text-red-400 text-sm">{errors.property_type.message}</p>
                )}
              </motion.div>
            )}

            {/* Step 2: Ownership Status */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-serif text-xl text-white mb-6">
                  Steht die Immobilie bereits in Ihrem Eigentum?
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {ownershipStatuses.map((status) => (
                    <RadioOption
                      key={status.value}
                      name="ownership_status"
                      value={status.value}
                      label={status.label}
                      currentValue={watchedValues.ownership_status}
                    />
                  ))}
                </div>
                {errors.ownership_status && (
                  <p className="text-red-400 text-sm">{errors.ownership_status.message}</p>
                )}
              </motion.div>
            )}

            {/* Step 3: Renovation Scope */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-serif text-xl text-white mb-6">
                  Wie umfangreich ist der Modernisierungsbedarf?
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  {renovationScopes.map((scope) => (
                    <RadioOption
                      key={scope.value}
                      name="renovation_scope"
                      value={scope.value}
                      label={scope.label}
                      currentValue={watchedValues.renovation_scope}
                    />
                  ))}
                </div>
                {errors.renovation_scope && (
                  <p className="text-red-400 text-sm">{errors.renovation_scope.message}</p>
                )}
              </motion.div>
            )}

            {/* Step 4: Timeline */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-serif text-xl text-white mb-6">
                  Wann möchten Sie das Projekt starten?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {projectTimelines.map((timeline) => (
                    <RadioOption
                      key={timeline.value}
                      name="project_timeline"
                      value={timeline.value}
                      label={timeline.label}
                      currentValue={watchedValues.project_timeline}
                    />
                  ))}
                </div>
                {errors.project_timeline && (
                  <p className="text-red-400 text-sm">{errors.project_timeline.message}</p>
                )}
              </motion.div>
            )}

            {/* Step 5: Contact Details */}
            {currentStep === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h3 className="font-serif text-xl text-white mb-6">
                  Wie können wir Sie erreichen?
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name" className="text-white/70 text-sm mb-2 block">
                      Vorname *
                    </Label>
                    <Input
                      id="first_name"
                      {...register('first_name')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-gold rounded-none"
                      placeholder="Max"
                      data-testid="input-first-name"
                    />
                    {errors.first_name && (
                      <p className="text-red-400 text-xs mt-1">{errors.first_name.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-white/70 text-sm mb-2 block">
                      Nachname *
                    </Label>
                    <Input
                      id="last_name"
                      {...register('last_name')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-gold rounded-none"
                      placeholder="Mustermann"
                      data-testid="input-last-name"
                    />
                    {errors.last_name && (
                      <p className="text-red-400 text-xs mt-1">{errors.last_name.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-white/70 text-sm mb-2 block">
                    E-Mail *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    {...register('email')}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-gold rounded-none"
                    placeholder="max@beispiel.de"
                    data-testid="input-email"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phone" className="text-white/70 text-sm mb-2 block">
                    Telefonnummer *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    {...register('phone')}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-gold rounded-none"
                    placeholder="0151 12345678"
                    data-testid="input-phone"
                  />
                  {errors.phone && (
                    <p className="text-red-400 text-xs mt-1">{errors.phone.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code" className="text-white/70 text-sm mb-2 block">
                      PLZ *
                    </Label>
                    <Input
                      id="postal_code"
                      {...register('postal_code')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-gold rounded-none"
                      placeholder="90402"
                      data-testid="input-postal-code"
                    />
                    {errors.postal_code && (
                      <p className="text-red-400 text-xs mt-1">{errors.postal_code.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-white/70 text-sm mb-2 block">
                      Ort *
                    </Label>
                    <Input
                      id="city"
                      {...register('city')}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-gold rounded-none"
                      placeholder="Nürnberg"
                      data-testid="input-city"
                    />
                    {errors.city && (
                      <p className="text-red-400 text-xs mt-1">{errors.city.message}</p>
                    )}
                  </div>
                </div>

                {/* Privacy Checkbox */}
                <div className="space-y-4 pt-4 border-t border-white/10">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="privacy_accepted"
                      checked={watchedValues.privacy_accepted}
                      onCheckedChange={(checked) => setValue('privacy_accepted', checked)}
                      className="mt-1 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                      data-testid="checkbox-privacy"
                    />
                    <Label htmlFor="privacy_accepted" className="text-white/70 text-sm leading-relaxed cursor-pointer">
                      Ich habe die{' '}
                      <a href="http://511816786.swh.strato-hosting.eu/datenschutz/" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                        Datenschutzerklärung
                      </a>{' '}
                      gelesen und erkläre mich damit einverstanden. *
                    </Label>
                  </div>
                  {errors.privacy_accepted && (
                    <p className="text-red-400 text-xs">{errors.privacy_accepted.message}</p>
                  )}

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="marketing_consent"
                      checked={watchedValues.marketing_consent}
                      onCheckedChange={(checked) => setValue('marketing_consent', checked)}
                      className="mt-1 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                      data-testid="checkbox-marketing"
                    />
                    <Label htmlFor="marketing_consent" className="text-white/70 text-sm leading-relaxed cursor-pointer">
                      Ich möchte über aktuelle Angebote und Neuigkeiten informiert werden (optional).
                    </Label>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            {currentStep > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="border-white/20 text-white hover:bg-white/5 rounded-none"
                data-testid="form-prev-btn"
              >
                <ArrowLeft size={18} className="mr-2" />
                Zurück
              </Button>
            ) : (
              <div />
            )}

            {currentStep < 5 ? (
              <Button
                type="button"
                onClick={nextStep}
                className="bg-gold text-black hover:bg-gold-light rounded-none"
                data-testid="form-next-btn"
              >
                Weiter
                <ArrowRight size={18} className="ml-2" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-gold text-black hover:bg-gold-light rounded-none min-w-[180px]"
                data-testid="form-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <SpinnerGap size={18} className="mr-2 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    Anfrage absenden
                    <ArrowRight size={18} className="ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default LeadFormSection;
